import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Play, Pause, PencilLine, Star } from "lucide-react";
import { getAvatarColor } from "../spacesPresenceUtils";
import { formatDuration } from "../spacesTimeUtils";

// The workspace's shared button styles (workspace.css).
const BTN_PRIMARY = "et-primary";
const BTN_OUTLINE = "et-secondary";
const BTN_GHOST = "et-ghost";

function InactiveCardShell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`et-space-card rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1] px-6 py-5 ${className ?? ""}`}
    >
      {children}
    </div>
  );
}

export type SpaceSummary = {
  spaceId: string;
  spaceName: string;
  description?: string | null;
  lessonContext: string | null;
  teacherName?: string | null;
  createdAt: string;
  snapshotSavedAt?: string | null;
  startedAt?: string | null;
  status: "open" | "closed" | "draft" | "paused";
  connectedCount?: number;
  connectedParticipants?: { userId: number; name: string; role: string }[];
  totalEnrolled?: number;
  classroomIds: number[];
  classroomNames: string[];
};

const AVATAR_SIZE = 28;

function TeacherAvatar({ userId, name }: { userId?: number; name: string }) {
  const color = getAvatarColor(userId ?? name);
  return (
    <div
      className="relative shrink-0"
      style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
    >
      <div
        title={`${name} (teacher)`}
        className="rounded-full flex items-center justify-center text-[11px] font-semibold border-2 w-full h-full"
        style={{
          backgroundColor: color.bg,
          color: color.text,
          borderColor: color.bg,
        }}
      >
        {name.charAt(0).toUpperCase()}
      </div>
      <Star
        className="absolute text-yellow-400 fill-yellow-400"
        style={{ width: 11, height: 11, top: 0, right: -2 }}
      />
    </div>
  );
}

function StudentAvatarStack({
  students,
}: {
  students: { userId: number; name: string }[];
}) {
  const visible = students.slice(0, 5);
  const overflow = students.length - visible.length;
  return (
    <div className="flex items-center">
      {visible.map((p, i) => {
        const color = getAvatarColor(p.userId);
        return (
          <div
            key={p.userId}
            title={p.name}
            className="rounded-full flex items-center justify-center text-[11px] font-semibold border-2 shrink-0"
            style={{
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              backgroundColor: color.bg,
              color: color.text,
              borderColor: color.bg,
              marginLeft: i === 0 ? 0 : -8,
            }}
          >
            {p.name.charAt(0).toUpperCase()}
          </div>
        );
      })}
      {overflow > 0 && (
        <div
          className="rounded-full flex items-center justify-center text-[11px] font-semibold border border-[#FBF8F1] bg-[#EDE8DC] text-[#687368] shrink-0"
          style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, marginLeft: -8 }}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}

function AvatarStack({
  participants,
}: {
  participants: { userId: number; name: string; role: string }[];
}) {
  const teacher = participants.find((p) => p.role === "teacher");
  const students = participants.filter((p) => p.role !== "teacher");

  const hasAnyone = teacher || students.length > 0;

  if (!hasAnyone) {
    return (
      <div
        className="rounded-full border border-dashed border-[#D6CEBF] shrink-0"
        style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
      />
    );
  }

  return (
    <div className="flex items-center gap-2">
      {teacher && <TeacherAvatar userId={teacher.userId} name={teacher.name} />}
      {teacher && students.length > 0 && (
        <div className="w-px h-4 bg-[#DED8CC] shrink-0" />
      )}
      {students.length > 0 && <StudentAvatarStack students={students} />}
    </div>
  );
}

function useLiveDuration(
  startedAt: string | null | undefined,
  active: boolean,
): string | null {
  const [, setTick] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!active || !startedAt) return;
    intervalRef.current = setInterval(() => setTick((t) => t + 1), 500);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active, startedAt]);

  if (!startedAt) return null;
  const ms = Date.now() - new Date(startedAt).getTime();
  return ms > 0 ? formatDuration(ms) : null;
}

export function LiveSpaceCard({
  space,
  onEnter,
  onClose,
  onPause,
  onResume,
  onEdit,
}: {
  space: SpaceSummary;
  onEnter: (space: SpaceSummary) => void;
  onClose?: (spaceId: string) => void;
  onPause?: (spaceId: string) => void;
  onResume?: (spaceId: string) => void;
  onEdit?: (spaceId: string) => void;
}) {
  const isPaused = space.status === "paused";
  const isActive = space.status === "open" || space.status === "paused";
  const duration = useLiveDuration(space.startedAt, isActive);
  const participants = space.connectedParticipants ?? [];
  const connectedStudentCount = participants.filter(
    (p) => p.role !== "teacher",
  ).length;
  const totalEnrolled = space.totalEnrolled ?? 0;

  return (
    <div
      className={`et-space-card rounded-[8px] border px-6 py-5 ${
        isPaused ? "border-amber-300 bg-amber-50" : "border-[#9AA884]"
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        {/* Left: badge + title + subtitle + KPIs */}
        <div className="flex flex-col gap-1.5 min-w-0 flex-1">
          {isPaused ? (
            <span className="inline-flex items-center gap-1.5 self-start px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold font-mulish border border-transparent">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
              PAUSED
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 self-start px-2 py-0.5 rounded-[4px] bg-[#EEF0E3] text-[#28543D] text-xs font-bold font-mulish">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shrink-0" />
              LIVE
            </span>
          )}

          <span className="text-xl font-semibold text-[#294B39] font-mulish leading-tight">
            {space.spaceName}
          </span>

          {(space.teacherName || space.lessonContext) && (
            <span className="text-sm text-[#687368] font-mulish truncate">
              {[
                space.teacherName ? `by ${space.teacherName}` : null,
                space.lessonContext,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          )}

          {space.description && (
            <span className="text-sm text-[#687368] font-mulish line-clamp-2">
              {space.description}
            </span>
          )}

          {/* KPI row */}
          <div className="flex items-end gap-6 mt-2">
            <div className="flex flex-col gap-1">
              <div className="flex items-baseline gap-1 leading-none">
                <span className="text-2xl font-bold text-[#294B39] font-mulish tabular-nums">
                  {connectedStudentCount}
                </span>
                {totalEnrolled > 0 && (
                  <span className="text-sm font-semibold text-[#687368] font-mulish tabular-nums">
                    / {totalEnrolled}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-semibold text-[#687368] font-mulish uppercase tracking-wide">
                Students joined
              </span>
            </div>

            {duration && (
              <div className="flex flex-col gap-1">
                <span className="text-2xl font-bold text-[#294B39] font-mulish tabular-nums leading-none">
                  {duration}
                </span>
                <span className="text-[10px] font-semibold text-[#687368] font-mulish uppercase tracking-wide">
                  Duration
                </span>
              </div>
            )}
          </div>

          <div className="mt-3">
            <AvatarStack participants={participants} />
          </div>
        </div>

        {/* Right: action buttons */}
        <div className="flex items-center gap-2 shrink-0">
          {onEdit && (
            <button onClick={() => onEdit(space.spaceId)} className={BTN_GHOST}>
              <PencilLine className="w-3.5 h-3.5" />
              Edit
            </button>
          )}
          {isPaused
            ? onResume && (
                <button
                  onClick={() => onResume(space.spaceId)}
                  className={BTN_OUTLINE}
                >
                  <Play className="w-3.5 h-3.5 fill-current" />
                  Resume
                </button>
              )
            : onPause && (
                <button
                  onClick={() => onPause(space.spaceId)}
                  className={BTN_OUTLINE}
                >
                  <Pause className="w-3.5 h-3.5 fill-current" />
                  Pause
                </button>
              )}
          {onClose && (
            <button
              onClick={() => onClose(space.spaceId)}
              className="et-danger"
            >
              End
            </button>
          )}
          <button onClick={() => onEnter(space)} className={BTN_PRIMARY}>
            Enter Space
          </button>
        </div>
      </div>
    </div>
  );
}

export function DraftSpaceCard({
  space,
  onEnter,
  onLaunch,
  onDelete,
  onEdit,
}: {
  space: SpaceSummary;
  onEnter: (space: SpaceSummary) => void;
  onLaunch: (spaceId: string) => void;
  onDelete: (spaceId: string) => void;
  onEdit?: (spaceId: string) => void;
}) {
  const savedAt = new Date(
    space.snapshotSavedAt ?? space.createdAt,
  ).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return (
    <InactiveCardShell>
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1.5 min-w-0">
          <span className="text-lg font-semibold text-[#294B39] font-mulish leading-tight">
            {space.spaceName}
          </span>
          <span className="text-sm text-[#687368] font-mulish truncate">
            {[space.lessonContext, `Saved ${savedAt}`]
              .filter(Boolean)
              .join(" · ")}
          </span>
          {space.description && (
            <span className="text-sm text-[#687368] font-mulish line-clamp-2">
              {space.description}
            </span>
          )}
          <span className="et-space-state">
            <span aria-hidden="true" />
            DRAFT
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => onDelete(space.spaceId)}
            className="et-ghost is-danger"
          >
            Delete
          </button>
          <button
            onClick={() => (onEdit ? onEdit(space.spaceId) : onEnter(space))}
            className={BTN_GHOST}
          >
            <PencilLine className="w-3.5 h-3.5" />
            Edit
          </button>
          <button
            onClick={() => onLaunch(space.spaceId)}
            className={BTN_PRIMARY}
          >
            Launch
          </button>
        </div>
      </div>
    </InactiveCardShell>
  );
}

export function ClosedSpaceCard({
  space,
  onRelaunch,
  onViewRecap,
  viewRecapLabel = "View Recap",
}: {
  space: SpaceSummary;
  onRelaunch?: (spaceId: string) => void;
  onViewRecap?: (spaceId: string) => void;
  viewRecapLabel?: string;
}) {
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  };
  // snapshotSavedAt is set on close — use it as closedAt when available
  const closedAt = new Date(
    space.snapshotSavedAt ?? space.createdAt,
  ).toLocaleString("en-US", dateOptions);
  return (
    <InactiveCardShell>
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1.5 min-w-0">
          <span className="text-lg font-semibold text-[#294B39] font-mulish leading-tight">
            {space.spaceName}
          </span>
          <span className="text-sm text-[#687368] font-mulish truncate">
            {[space.teacherName ? `by ${space.teacherName}` : null, closedAt]
              .filter(Boolean)
              .join(" · ")}
          </span>
          {space.description && (
            <span className="text-sm text-[#687368] font-mulish line-clamp-2">
              {space.description}
            </span>
          )}
          <span className="et-space-state mt-2">
            <span aria-hidden="true" />
            CLOSED
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {onViewRecap && (
            <button
              onClick={() => onViewRecap(space.spaceId)}
              className={BTN_GHOST}
            >
              {viewRecapLabel}
            </button>
          )}
          {onRelaunch && (
            <button
              onClick={() => onRelaunch(space.spaceId)}
              className={BTN_PRIMARY}
            >
              Relaunch
            </button>
          )}
        </div>
      </div>
    </InactiveCardShell>
  );
}
